export type Football3dTeamCode =
  | 'MIL'
  | 'DTM'
  | 'SIV'
  | 'ATM'
  | 'RMD'
  | 'INT'
  | 'CHE'
  | 'BAR'
  | 'LIV'
  | 'PSG'
  | 'JUV'
  | 'CEL'
  | 'BAY'
  | 'AJA'
  | 'POR'
  | 'MUN'
  | 'NAP'
  | 'ROM'
  | 'BIL'
  | 'MAC';

export type RgbTuple = readonly [number, number, number];

export type Football3dTeamTheme = Readonly<{
  bg0: `#${string}`;
  bg1: `#${string}`;
  bg2: `#${string}`;
  accentRgb: RgbTuple;
  accent2Rgb?: RgbTuple;
  overlayRgb?: RgbTuple;
}>;

// Copied from platform-tv `football3dTeamThemes.ts` (keep in sync for parity).
export const FOOTBALL3D_TEAM_THEME_BY_CODE: Readonly<Record<Football3dTeamCode, Football3dTeamTheme>> = {
  MIL: { bg0: '#130607', bg1: '#2b0a0c', bg2: '#0b0b0d', accentRgb: [255, 48, 48], overlayRgb: [255, 48, 48] },
  DTM: { bg0: '#0b0b0d', bg1: '#161718', bg2: '#0b0b0d', accentRgb: [255, 210, 58], overlayRgb: [255, 210, 58] },
  SIV: { bg0: '#140406', bg1: '#2a0b10', bg2: '#120206', accentRgb: [255, 92, 92], overlayRgb: [255, 92, 92] },
  ATM: { bg0: '#070a1d', bg1: '#0e1336', bg2: '#070a1d', accentRgb: [255, 51, 51], overlayRgb: [255, 51, 51] },
  RMD: { bg0: '#0d0f13', bg1: '#1c212a', bg2: '#0d0f13', accentRgb: [180, 188, 205], overlayRgb: [180, 188, 205] },
  INT: { bg0: '#060913', bg1: '#0a1630', bg2: '#060913', accentRgb: [213, 178, 89], overlayRgb: [36, 112, 255] },
  CHE: { bg0: '#05031b', bg1: '#120a4a', bg2: '#05031b', accentRgb: [125, 146, 255], overlayRgb: [125, 146, 255] },
  BAR: { bg0: '#071031', bg1: '#2a0b22', bg2: '#071031', accentRgb: [244, 196, 45], accent2Rgb: [196, 31, 45], overlayRgb: [244, 196, 45] },
  LIV: { bg0: '#220205', bg1: '#3a060a', bg2: '#220205', accentRgb: [255, 184, 59], overlayRgb: [255, 71, 71] },
  PSG: { bg0: '#0b0750', bg1: '#14108a', bg2: '#0b0750', accentRgb: [255, 56, 56], accent2Rgb: [240, 190, 70], overlayRgb: [255, 56, 56] },
  JUV: { bg0: '#0d0d0e', bg1: '#1a1a1c', bg2: '#0d0d0e', accentRgb: [245, 232, 198], overlayRgb: [245, 232, 198] },
  CEL: { bg0: '#062214', bg1: '#0c3a22', bg2: '#062214', accentRgb: [0, 196, 98], overlayRgb: [0, 196, 98] },
  BAY: { bg0: '#200006', bg1: '#3a000d', bg2: '#200006', accentRgb: [255, 255, 255], overlayRgb: [155, 0, 26] },
  AJA: { bg0: '#240003', bg1: '#420006', bg2: '#240003', accentRgb: [255, 56, 56], overlayRgb: [255, 56, 56] },
  POR: { bg0: '#021024', bg1: '#012b5a', bg2: '#021024', accentRgb: [0, 110, 255], overlayRgb: [0, 110, 255] },
  MUN: { bg0: '#240003', bg1: '#520006', bg2: '#240003', accentRgb: [255, 201, 75], overlayRgb: [255, 61, 61] },
  NAP: { bg0: '#03122b', bg1: '#062d62', bg2: '#03122b', accentRgb: [121, 178, 255], overlayRgb: [121, 178, 255] },
  ROM: { bg0: '#2a0404', bg1: '#4c0808', bg2: '#2a0404', accentRgb: [255, 214, 79], overlayRgb: [255, 214, 79] },
  BIL: { bg0: '#130102', bg1: '#3a0507', bg2: '#130102', accentRgb: [196, 167, 90], overlayRgb: [196, 167, 90] },
  MAC: { bg0: '#001429', bg1: '#003a66', bg2: '#001429', accentRgb: [102, 190, 255], overlayRgb: [102, 190, 255] },
} as const;

const DEFAULT_THEME: Football3dTeamTheme = {
  bg0: '#0a1628',
  bg1: '#1a2d4a',
  bg2: '#0a1628',
  accentRgb: [255, 208, 58],
  overlayRgb: [255, 208, 58],
};

function clampRgb(v: number): number {
  if (!Number.isFinite(v)) return 0;
  return Math.max(0, Math.min(255, Math.round(v)));
}

function rgbToVarValue(rgb: RgbTuple): string {
  const [r, g, b] = rgb;
  return `${clampRgb(r)}, ${clampRgb(g)}, ${clampRgb(b)}`;
}

export function getFootball3dTeamTheme(teamCode: string): Football3dTeamTheme {
  const code = String(teamCode || '').toUpperCase() as Football3dTeamCode;
  return (FOOTBALL3D_TEAM_THEME_BY_CODE as any)?.[code] ?? DEFAULT_THEME;
}

export function getFootball3dTeamThemeCssVars(teamCode: string): Readonly<Record<string, string>> {
  const theme = getFootball3dTeamTheme(teamCode);
  const accent2 = theme.accent2Rgb ?? theme.accentRgb;
  const overlay = theme.overlayRgb ?? theme.accentRgb;
  return {
    '--ft-bg0': theme.bg0,
    '--ft-bg1': theme.bg1,
    '--ft-bg2': theme.bg2,
    '--ft-accent-rgb': rgbToVarValue(theme.accentRgb),
    '--ft-accent2-rgb': rgbToVarValue(accent2),
    '--ft-overlay-rgb': rgbToVarValue(overlay),
    '--ft-border-rgb': rgbToVarValue(theme.accentRgb),
  };
}

